import './coapplicantDifferentResidenceTypePage.scss';

import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NavigationPane } from '../../../components/shared_new/NavigationPane/NavigationPane';
import { RadioGroup } from '../../../components/shared_new/RadioGroup/RadioGroup';
import { RadioOptionClickEvent } from '../../../components/shared_new/RadioGroup/types/RadioOptionClickEvent';
import { ResidenceTypeEnum } from '../../../store/applicantStore/enums/ResidenceTypeEnum';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';
import { isValidClickEvent } from '../../../utils/component/isValidClickEvent';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { setDrawerOpen } = useNavigationPaneStore();

  const [isInitialSelection, setIsInitialSelection] = useState(false);

  const {
    computed: { progress },
    next,
    previous,
  } = useWorkflowStore();

  const {
    computed: { isCoapplicantPersisted },
    coapplicantCurrentResidenceType,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantCurrentResidenceType,
    syncCoapplicant,
  } = useCoapplicantStore();

  const {
    formState: { isDirty },
    handleSubmit,
    register,
    watch,
  } = useForm({
    defaultValues: {
      residenceTypeValue: coapplicantCurrentResidenceType,
    },
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const radioOptionClicked = (
    event: RadioOptionClickEvent<ResidenceTypeEnum>,
  ): void => {
    if (!('target' in event && 'value' in event.target)) {
      return;
    }

    const residenceTypeValue = event.target.value;

    setIsInitialSelection(
      isEmpty(coapplicantCurrentResidenceType) && isValidClickEvent(event),
    );

    setCoapplicantCurrentResidenceType(residenceTypeValue);
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { residenceTypeValue } = values;

    if (!residenceTypeValue) {
      return;
    }

    if (isCoapplicantPersisted && isDirty) {
      const synced = await syncCoapplicant();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  useEffect(() => {
    if (isInitialSelection) {
      handleSubmit(formSubmit)();
    }
  }, [isInitialSelection]);

  return (
    <ThemeProvider theme={theme}>
      <Box
        className="coapplicant-different-residence-type-page"
        sx={theme.pageLayout}
      >
        <header
          data-testid={
            locator.pages.coapplicantDifferentResidenceType.header.root
          }
        >
          <button
            aria-label="Previous step"
            data-testid={
              locator.pages.coapplicantDifferentResidenceType.header
                .previousStepButton
            }
            type="button"
            onClick={previousStep}
          >
            <KeyboardBackspaceRoundedIcon />
          </button>
          <button
            aria-label="Toggle drawer"
            data-testid={
              locator.pages.coapplicantDifferentResidenceType.header
                .drawerButton
            }
            type="button"
            onClick={() => {
              setDrawerOpen(true);
            }}
          >
            <MenuRoundedIcon />
          </button>
        </header>

        <div className="progress-container" role="complementary">
          <LinearProgress
            aria-label="Application completed percentage"
            data-testid={
              locator.pages.coapplicantDifferentResidenceType.progress.root
            }
            value={progress}
            variant="determinate"
          />
        </div>

        <main>
          <form
            data-testid={
              locator.pages.coapplicantDifferentResidenceType.form.root
            }
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1
                data-testid={
                  locator.pages.coapplicantDifferentResidenceType.form.h1
                }
              >
                {t('CoapplicantDifferentResidenceTypePage.title')}
              </h1>
              <h2
                data-testid={
                  locator.pages.coapplicantDifferentResidenceType.form.h2
                }
              >
                {t('CoapplicantDifferentResidenceTypePage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.coapplicantDifferentResidenceType.alert
                        .error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <RadioGroup
                  defaultValue={coapplicantCurrentResidenceType || ''}
                  name="residenceTypeValue"
                  options={[
                    {
                      label: t('CoapplicantDifferentResidenceTypePage.own'),
                      value: ResidenceTypeEnum.OWN,
                      testId:
                        locator.pages.coapplicantDifferentResidenceType.form
                          .radioOption1,
                    },
                    {
                      label: t('CoapplicantDifferentResidenceTypePage.rent'),
                      value: ResidenceTypeEnum.RENT,
                      testId:
                        locator.pages.coapplicantDifferentResidenceType.form
                          .radioOption2,
                    },
                    {
                      label: t('CoapplicantDifferentResidenceTypePage.other'),
                      value: ResidenceTypeEnum.OTHER,
                      testId:
                        locator.pages.coapplicantDifferentResidenceType.form
                          .radioOption3,
                    },
                  ]}
                  register={register}
                  testId={
                    locator.pages.coapplicantDifferentResidenceType.form
                      .radioGroupRoot
                  }
                  watch={watch}
                  onClick={radioOptionClicked}
                />
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <LoadingButton
                  color="primary"
                  data-testid={
                    locator.pages.coapplicantDifferentResidenceType.form
                      .submitButton
                  }
                  disabled={isEmpty(coapplicantCurrentResidenceType)}
                  loading={isSyncingCoapplicant}
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('CoapplicantDifferentResidenceTypePage.continue')}
                </LoadingButton>
              </div>
            </div>
            <footer
              data-testid={
                locator.pages.coapplicantDifferentResidenceType.footer.root
              }
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantDifferentResidenceTypePage';
